/**
 * useDemoRunner.js
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-464 · Phase 5.5b · P&L Demo Runner Hook
 * Date:     2026-09-02
 * Umbrella: IJ-076 · Refactor Programme 2026 · Phase 5
 * Owner:    E1
 *
 * Purpose:
 *   Lift the P&L Demo cluster out of KnowledgeGraphPage.jsx into a
 *   discrete state slice. Every state declaration, the coach-mark
 *   effect and the handleRunDemo body below are byte-verbatim with the
 *   pre-extraction parent. Zero behaviour change.
 *
 * Owns:
 *   - showDemoIntro (P&L Demo intro modal visibility).
 *   - Download coach-mark state: showDownloadCoachMark, forecastTooltip,
 *     downloadCoachMarkFiredRef.
 *   - The coach-mark auto-show effect (first demo render this session).
 *   - handleRunDemo (fires the pre-loaded sample-budget forecast; does
 *     NOT decrement the query quota).
 *
 * Parent-owned pieces this hook mutates arrive as arguments. demoViewed
 * and setDemoViewed come from useQuotaExitBanner, so this hook is called
 * immediately below that hook in the parent.
 */
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';

export function useDemoRunner({
  api,
  API,
  setQuery,
  setQueryThread,
  setIsForecastLoading,
  setForecastResult,
  forecastResult,
  setMediaCache,
  setMediaTab,
  setMediaLoading,
  setMediaError,
  followUpRef,
  demoViewed,
  setDemoViewed,
}) {
  const [showDemoIntro, setShowDemoIntro] = useState(false);
  // Coach-mark above the Download button. Auto-shows on the FIRST demo
  // render in the session (false -> true transition of demoViewed); never
  // re-fires even if user closes & re-runs the demo. After dismissal the
  // button keeps a standard hover tooltip with the same copy.
  const [showDownloadCoachMark, setShowDownloadCoachMark] = useState(false);
  // D6 fix — hover-positioned tooltip rendered via Portal to escape the
  // dashboard's backdrop-filter stacking context. {top, right} in fixed
  // viewport coordinates; null = hidden.
  const [forecastTooltip, setForecastTooltip] = useState(null);
  const downloadCoachMarkFiredRef = useRef(false);
  useEffect(() => {
    if (demoViewed && forecastResult && !downloadCoachMarkFiredRef.current) {
      downloadCoachMarkFiredRef.current = true;
      // Give the scroll-into-view (line ~2050) a moment to land first.
      const t = setTimeout(() => setShowDownloadCoachMark(true), 700);
      return () => clearTimeout(t);
    }
  }, [demoViewed, forecastResult]);

  const handleRunDemo = async () => {
    setShowDemoIntro(false);
    // Surface the demo title in the top query input so it's clear what the
    // user is currently viewing — replaces any stale prior query (e.g. the
    // user's just-completed "Jessica Alba" search).
    setQuery('P&L Demo — 2026 Sample Budget');
    // Push synthetic forecast_prompt thread item so existing dashboard renderer fires.
    // is_demo flag excludes from the 3-query quota counter.
    setQueryThread([{
      query: 'P&L Demo — 2026 Sample Budget',
      result: {
        answer: '',
        citations: [],
        web_results: [],
        product_results: null,
        intent_layer: null,
        meta: { is_demo: true },
        ethics_layer: null,
        source_entities: [],
        source_documents: [],
        related_concepts: [],
        images: [],
        suggestions: [],
        version: '',
        type: 'forecast_prompt',
        is_demo: true,
      },
      is_demo: true,
    }]);
    // Land the user at the very top so they see, in order:
    //   1. the populated query field with "P&L Demo — 2026 Sample Budget"
    //   2. the purple "Analysing budget data..." loading notification bar
    // The forecast dashboard will render below; a second scroll on completion
    // brings the user to the top of the dashboard itself.
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    setIsForecastLoading(true);
    setForecastResult(null);
    try {
      const res = await api.post(`${API}/demo/pl-forecast`);
      setForecastResult(res.data);
      setDemoViewed(true);
      toast.success('P&L Demo generated');
      // Land the user on the TOP of the rendered dashboard (the purple
      // header bar with title + status badge + Download button) — never
      // below it. They can scroll down at their own pace from there.
      // Reserve ~150 px of headroom above the dashboard so the
      // first-run download coach-mark (which sits above the Download
      // button) lands inside the viewport instead of off-screen.
      setTimeout(() => {
        const node = document.querySelector('[data-testid="forecast-result-inline"]');
        if (node) {
          const HEADROOM_PX = 150;
          const top = node.getBoundingClientRect().top + window.pageYOffset - HEADROOM_PX;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      }, 300);
      // D4-6 (Jul 13, 2026): after the demo lands, drop the caret into
      // the follow-up "Search-it" input so the user can start typing
      // their next question immediately. Mirrors the handleQuery
      // post-submit focus at L2086. Longer 500 ms delay because the
      // demo scroll + result render take a beat longer to settle than a
      // normal query.
      setTimeout(() => {
        if (followUpRef.current) {
          try { followUpRef.current.focus({ preventScroll: true }); }
          catch { followUpRef.current.focus(); }
        }
      }, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Demo failed to run');
      setQueryThread([]);
      // Clear media cache when the thread resets — otherwise the next
      // query at the same idx serves the previous query's images/videos
      // (Feb 28, 2026 — D3 fix).
      setMediaCache({}); setMediaTab({}); setMediaLoading({}); setMediaError({});
    } finally {
      setIsForecastLoading(false);
    }
  };

  return {
    showDemoIntro,
    setShowDemoIntro,
    showDownloadCoachMark,
    setShowDownloadCoachMark,
    forecastTooltip,
    setForecastTooltip,
    handleRunDemo,
  };
}
